import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { AvailabilityResult } from "../../api/registration";
import { useAvailabilityMessage, useCheckAvailability } from "../../api/registration";
import { isTranslatedVersion, legalDocumentPath } from "../../legal/documents";
import {
  DEFAULT_PASSWORD_POLICY,
  describePolicy,
  usePasswordPolicy,
  validatePassword,
  type TPasswordPolicy,
} from "../../api/passwordPolicy";

export interface AccountFormValues {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  password_confirmation: string;
  company_name: string;
  vat: string;
  registration_id: string;
  accept_terms: boolean;
  accept_dpa: boolean;
}

export const emptyAccountForm: AccountFormValues = {
  firstname: "",
  lastname: "",
  email: "",
  password: "",
  password_confirmation: "",
  company_name: "",
  vat: "",
  registration_id: "",
  accept_terms: false,
  accept_dpa: false,
};

/**
 * DEPRECIE : conserve pour ne pas casser un import externe. La longueur
 * minimale fait desormais partie de la politique servie par l'API
 * (GET /auth/password-policy) et lue par usePasswordPolicy().
 *
 * La constante etait "alignee a la main" sur la regle du serveur, avec un
 * commentaire demandant de ne pas les laisser diverger. Elles avaient
 * diverge : le serveur exigeait 8 caracteres et rien d'autre, l'ecran
 * d'invitation en exigeait quatre classes, et l'ecran de reinitialisation
 * n'exigeait qu'un symbole. Une consigne en commentaire ne tient pas quatre
 * fichiers alignes.
 */
export const PASSWORD_MIN = 8;

type Errors = Partial<Record<keyof AccountFormValues, string>>;

export const validateAccountForm = (
  v: AccountFormValues,
  t: (k: string, d?: any) => string,
  policy: TPasswordPolicy = DEFAULT_PASSWORD_POLICY
): Errors => {
  const e: Errors = {};
  if (!v.firstname.trim()) e.firstname = t("required.firstname", "First name is required");
  if (!v.lastname.trim()) e.lastname = t("required.lastname", "Name is required");
  if (!v.email.trim()) e.email = t("required.email", "Email is required");
  else if (!/^\S+@\S+\.\S+$/.test(v.email.trim()))
    e.email = t("required.emailInvalid", "Email is invalid");
  if (!v.company_name.trim() || v.company_name.trim().length < 2)
    e.company_name = t("register.errorCompanyName", "Company name is required");
  // La politique complete, pas seulement la longueur : le serveur refuse
  // desormais un mot de passe de 8 caracteres tout en minuscules, et le
  // formulaire doit le dire avant l'envoi plutot que de laisser un 422
  // remonter.
  if (!validatePassword(v.password, policy))
    e.password = describePolicy(t, policy);
  if (v.password !== v.password_confirmation)
    e.password_confirmation = t("login.errorConfirm", "Passwords do not match");
  // Deux consentements distincts, deux erreurs distinctes. Les fusionner sous
  // un seul message ("acceptez les conditions") laisserait l'utilisateur qui a
  // coché une seule case chercher laquelle manque — et surtout, le DPA et les
  // CGU sont deux actes juridiques separes : les traiter comme un bloc revient
  // a faire signer l'un pour l'autre.
  if (!v.accept_terms)
    e.accept_terms = t("register.errorAcceptTerms", "You must accept the Terms of Service");
  if (!v.accept_dpa)
    e.accept_dpa = t(
      "register.errorAcceptDpa",
      "You must accept the Data Processing Agreement"
    );

  // `vat` is a RATE (company.vat is decimal(10,2)), not a VAT identification
  // number. The field used to be free text sharing a label with the settings
  // rate dropdown, so anyone typing "FR12345678901" had it silently discarded
  // by the column. Validated here so the user sees it inline instead of a 422.
  if (v.vat.trim().length > 0) {
    const rate = Number(v.vat.trim().replace(",", "."));
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      e.vat = t(
        "register.errorVatRate",
        "Enter a VAT rate between 0 and 100 (for example 20)"
      );
    }
  }

  return e;
};

interface Props {
  values: AccountFormValues;
  onChange: (v: AccountFormValues) => void;
  onSubmit: () => void;
  submitting?: boolean;
}

/**
 * Step 1 — who you are and which company you are registering.
 *
 * The email is checked against the API on blur rather than on every keystroke:
 * the endpoint is IP-throttled and per-keystroke calls would burn the budget
 * before the user finishes typing their address.
 */
export const AccountStep: React.FC<Props> = ({ values, onChange, onSubmit, submitting }) => {
  const { t, i18n } = useTranslation();
  const [errors, setErrors] = useState<Errors>({});
  const [availability, setAvailability] = useState<AvailabilityResult | null>(null);

  const checkAvailability = useCheckAvailability();
  const availabilityMessage = useAvailabilityMessage();

  const set = (k: keyof AccountFormValues) => (value: any) => {
    onChange({ ...values, [k]: value });
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
    if (k === "email") setAvailability(null);
  };

  const runAvailability = () => {
    const email = values.email.trim();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;
    checkAvailability.mutate(email, { onSuccess: setAvailability });
  };

  const blockingMessage = useMemo(
    () => availabilityMessage(availability),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availability]
  );

  /*
   * Cibles des deux liens de consentement : des ECRANS, plus des fichiers.
   *
   * C'etait `legalDocumentUrl(...)`, donc `/legal/Terms_of_Service_FR.docx`.
   * Ces fichiers ne sont pas dans le depot (public/legal/ ne contient qu'un
   * README) : les deux liens repondaient 404, au moment precis ou l'utilisateur
   * verifie ce qu'il signe. Et meme deposes, un .docx se telecharge au lieu de
   * s'afficher -- dans Word sur poste, souvent nulle part sur mobile.
   *
   * Les chemins ne portent pas la langue : la page lit la locale i18next
   * courante et sert la traduction disponible la plus proche, en annoncant
   * elle-meme quand elle retombe sur l'anglais. Plus rien a recalculer ici,
   * d'ou la disparition des deux useMemo.
   */
  const termsPath = legalDocumentPath("terms");
  const dpaPath = legalDocumentPath("dpa");
  const translated = isTranslatedVersion(i18n.language);
  const passwordPolicy = usePasswordPolicy();

  const handleSubmit = () => {
    const e = validateAccountForm(values, t, passwordPolicy);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    // A known-bad email blocks submission; an unchecked one does not — the
    // server re-validates, so a failed availability call must not trap the user.
    if (availability && !availability.available) return;
    onSubmit();
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          fullWidth
          required
          label={t("collaborator.firstName", "First name")}
          value={values.firstname}
          onChange={(e) => set("firstname")(e.target.value)}
          error={!!errors.firstname}
          helperText={errors.firstname}
          autoComplete="given-name"
        />
        <TextField
          fullWidth
          required
          label={t("collaborator.name", "Name")}
          value={values.lastname}
          onChange={(e) => set("lastname")(e.target.value)}
          error={!!errors.lastname}
          helperText={errors.lastname}
          autoComplete="family-name"
        />
      </Stack>

      <TextField
        fullWidth
        required
        type="email"
        label={t("register.workEmail", "Work email")}
        value={values.email}
        onChange={(e) => set("email")(e.target.value)}
        onBlur={runAvailability}
        error={!!errors.email || (!!availability && !availability.available)}
        helperText={errors.email}
        autoComplete="email"
        InputProps={{
          endAdornment: checkAvailability.isPending ? (
            <CircularProgress size={16} />
          ) : null,
        }}
      />

      {blockingMessage && (
        <Alert severity="warning" sx={{ alignItems: "center" }}>
          {blockingMessage}{" "}
          {availability?.reason === "email_taken" && (
            <MuiLink href="/" underline="hover">
              {t("login.login", "Login")}
            </MuiLink>
          )}
        </Alert>
      )}

      <TextField
        fullWidth
        required
        label={t("register.companyName", "Company name")}
        value={values.company_name}
        onChange={(e) => set("company_name")(e.target.value)}
        error={!!errors.company_name}
        helperText={errors.company_name}
        autoComplete="organization"
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          fullWidth
          label={t("register.vatRate", "VAT rate (%)")}
          value={values.vat}
          onChange={(e) => set("vat")(e.target.value)}
          error={!!errors.vat}
          helperText={errors.vat || t("common.optional", "Optional")}
          inputProps={{ inputMode: "decimal" }}
          placeholder="20"
        />
        <TextField
          fullWidth
          label={t("register.registrationId", "Company registration number")}
          value={values.registration_id}
          onChange={(e) => set("registration_id")(e.target.value)}
          helperText={t("common.optional", "Optional")}
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          fullWidth
          required
          type="password"
          label={t("setting.newPasswordLabel", "New password")}
          value={values.password}
          onChange={(e) => set("password")(e.target.value)}
          error={!!errors.password}
          helperText={errors.password || t("setting.passwordHint", "")}
          autoComplete="new-password"
        />
        <TextField
          fullWidth
          required
          type="password"
          label={t("login.confirmPassword", "Confirm password")}
          value={values.password_confirmation}
          onChange={(e) => set("password_confirmation")(e.target.value)}
          error={!!errors.password_confirmation}
          helperText={errors.password_confirmation}
          autoComplete="new-password"
        />
      </Stack>

      {/*
        Les deux consentements, dans l'ordre ou ils sont opposables : les CGU
        etablissent la relation contractuelle, le DPA ne regit que le
        traitement des donnees effectue DANS ce cadre. Le second n'a pas de
        sens avant le premier.

        Chaque case porte son propre lien. Une case dont le document n'est pas
        atteignable depuis l'endroit ou on la coche n'est pas un consentement
        eclaire, c'est une formalite — et c'est exactement ce que le RGPD
        refuse de reconnaitre pour un DPA.
      */}
      <Box>
        <FormControlLabel
          sx={{ alignItems: "flex-start", mr: 0 }}
          control={
            <Checkbox
              checked={values.accept_terms}
              onChange={(e) => set("accept_terms")(e.target.checked)}
              inputProps={{ "aria-describedby": "accept-terms-error" }}
              sx={{ pt: 0.5 }}
            />
          }
          label={
            /*
              <Trans> et pas une concatenation `prefixe + lien`. L'allemand
              place l'objet au milieu (« Ich habe die … gelesen und akzeptiere
              sie ») et le francais elide l'article devant « Accord » : coller
              deux cles bout a bout impose l'ordre des mots anglais a six
              langues et produit « j'accepte l' Accord ».
            */
            <Typography fontSize={13.5}>
              <Trans
                i18nKey="register.acceptTerms"
                defaults="I have read and accept the <doc>Terms of Service</doc>"
                components={{
                  doc: (
                    /*
                      Nouvel onglet, deliberement : le formulaire compte huit
                      champs deja saisis. Naviguer dans le meme onglet les
                      perdrait, et lire un contrat n'a pas a couter une
                      re-saisie complete.

                      stopPropagation empeche le clic sur le lien de basculer la
                      case : le label entier est cliquable dans un
                      FormControlLabel, donc sans lui on cocherait le
                      consentement en essayant justement d'aller le lire.
                    */
                    <MuiLink
                      component={RouterLink}
                      to={termsPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  ),
                }}
              />
            </Typography>
          }
        />
        {errors.accept_terms && (
          <Typography id="accept-terms-error" color="error" fontSize={12} ml={4}>
            {errors.accept_terms}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: -1.5 }}>
        <FormControlLabel
          sx={{ alignItems: "flex-start", mr: 0 }}
          control={
            <Checkbox
              checked={values.accept_dpa}
              onChange={(e) => set("accept_dpa")(e.target.checked)}
              inputProps={{ "aria-describedby": "accept-dpa-error" }}
              sx={{ pt: 0.5 }}
            />
          }
          label={
            <Typography fontSize={13.5}>
              <Trans
                i18nKey="register.acceptDpa"
                defaults="I have read and accept the <doc>Data Processing Agreement</doc>"
                components={{
                  doc: (
                    <MuiLink
                      component={RouterLink}
                      to={dpaPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  ),
                }}
              />
            </Typography>
          }
        />
        {errors.accept_dpa && (
          <Typography id="accept-dpa-error" color="error" fontSize={12} ml={4}>
            {errors.accept_dpa}
          </Typography>
        )}
      </Box>

      {/*
        La declaration recapitulative. Les deux cases disent ce qui est lu, ce
        paragraphe dit ce qui est engage — et il nomme le produit, parce que
        c'est CodeNekt For Fleet OÜ qui contracte, pas "le site".
      */}
      <Typography fontSize={13} color="text.secondary">
        {t(
          "register.legalConsentStatement",
          "I agree to the CodeNekt for Fleet Terms of Service and Data Processing Agreement."
        )}
        {translated && (
          <>
            {" "}
            {t(
              "register.legalEnglishPrevails",
              "The English version prevails in case of discrepancy."
            )}
          </>
        )}
      </Typography>

      <Button
        variant="contained"
        size="large"
        onClick={handleSubmit}
        /*
          Desactive tant que les deux cases ne sont pas cochees : l'API rejette
          de toute facon la requete (422), et faire echouer un formulaire de 8
          champs pour une case oubliee est une punition, pas un retour d'erreur.
          La validation au clic reste en place pour le reste du formulaire.
        */
        disabled={submitting || !values.accept_terms || !values.accept_dpa}
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {t("register.continueToPlan", "Continue")}
      </Button>

      <Typography fontSize={13} color="text.secondary" textAlign="center">
        {t("register.alreadyHaveAccount", "Already have an account?")}{" "}
        <MuiLink href="/" underline="hover">
          {t("login.login", "Login")}
        </MuiLink>
      </Typography>
    </Stack>
  );
};

export default AccountStep;
